import axios from 'axios';
import * as Sentry from '@sentry/vue';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || 'http://localhost:8081/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send HttpOnly cookie on every request
});

// Attach in-memory token when available (SSE fetch calls use this too)
api.interceptors.request.use((config) => {
  // Prefer in-memory Pinia token; fall back to localStorage for backward compat
  // during session where the page was loaded before this change was deployed
  const token = (window as any).__rileyToken || localStorage.getItem('_riley');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Dead-API reporting ───────────────────────────────────────────────────────
// Stores deliberately swallow failures so a broken endpoint never breaks the UI
// (notificationStore's `catch {}` is the clearest example). That is right for the
// customer and wrong for us: /customer/notifications was failing in the browser
// console for days and never reached Sentry. This reports the failures the stores
// hide, without changing one thing about what the customer sees.
//
// Reported: no HTTP response at all (network down, CORS, DNS, TLS, timeout) and
// 5xx. NOT reported: 4xx — 401/403/422 are ordinary, expected traffic in this app
// (the boot session probe 401s every time a signed-out customer loads a page) —
// and not requests the user or the app cancelled.

// Debounce map: "METHOD /path" -> epoch ms of the last event we sent.
// notificationStore polls every 60s and TrackOrder re-polls too, so a single dead
// endpoint left broken over a weekend would otherwise be thousands of identical
// Sentry events (and quota). One event per endpoint per 5 minutes is enough to
// see that it is down and how long it stayed down, which is all we need.
const API_FAILURE_FLOOR_MS = 5 * 60 * 1000;
const lastReportedAt = new Map<string, number>();

// Requests in flight when the tab is closing or the app hard-redirects (the 401
// handler below does `window.location.href = '/login'`) abort with no response.
// Those are not outages, so stop reporting once the page is on its way out.
let pageIsUnloading = false;
window.addEventListener('pagehide', () => { pageIsUnloading = true; });
window.addEventListener('beforeunload', () => { pageIsUnloading = true; });

/** Strip the query string and collapse id segments, so /invoices/41 and
 *  /invoices/77 share one debounce bucket and one Sentry issue. */
function normalisePath(url: string): string {
  const path = url.split('?')[0];
  return path
    .split('/')
    .map((seg) => (/^\d+$/.test(seg) ? ':id' : seg))
    .join('/');
}

function reportApiFailure(err: any) {
  if (pageIsUnloading) return;
  if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;

  const status: number | undefined = err?.response?.status;
  const hasResponse = err?.response !== undefined;
  // 4xx is normal traffic; anything else that failed is worth knowing about.
  if (hasResponse && (status ?? 0) < 500) return;

  const method = String(err?.config?.method || 'get').toUpperCase();
  const path = normalisePath(String(err?.config?.url || 'unknown'));
  const key = `${method} ${path}`;

  const now = Date.now();
  const last = lastReportedAt.get(key) ?? 0;
  if (now - last < API_FAILURE_FLOOR_MS) return;
  lastReportedAt.set(key, now);

  try {
    Sentry.captureException(err, {
      // A request that never got a response is almost always the caller's
      // connection — one browser, one network, one afternoon. It is worth
      // seeing in aggregate, because a real outage makes it spike, but it is
      // not an error in the sense a 500 is, and leaving it at error level puts
      // somebody's dropped wifi in the same list as a broken endpoint.
      level: hasResponse ? 'error' : 'warning',
      tags: { api_failure: 'true', no_response: String(!hasResponse) },
      // Group by endpoint rather than by the axios stack frame, which is identical
      // for every failing call and would merge unrelated endpoints into one issue.
      fingerprint: ['api_failure', method, path, String(status ?? 'no-response')],
      extra: {
        method,
        path,
        status: status ?? null,
        // No response at all: network error, CORS rejection, DNS, TLS or timeout.
        no_response: !hasResponse,
        code: err?.code ?? null,
        base_url: api.defaults.baseURL,
      },
    });
  } catch {
    // Reporting must never turn a failed request into a thrown one.
  }
}

// On 401, clear session and redirect to login — but NEVER for the auth-probe
// endpoints (login + the boot-time session check legitimately 401 when signed
// out), and never when already on /login. Otherwise the session check on every
// page load 401s → hard-reload → session check → reload… an infinite loop.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    reportApiFailure(err);

    const url: string = err.config?.url || '';
    const isAuthProbe = url.includes('auth/login') || url.includes('auth/session');
    if (
      err.response?.status === 401 &&
      !isAuthProbe &&
      window.location.pathname !== '/login'
    ) {
      localStorage.removeItem('_riley');
      localStorage.removeItem('_riley_user');
      (window as any).__rileyToken = null;
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;
